import { Link } from "react-router-dom";
import { ArrowRight, Phone, Siren, ClipboardList, Boxes, Truck, Users, ShieldCheck, Clock, HeartHandshake, Eye } from "lucide-react";
import { SITE, IMG } from "@/lib/site";
import Chapter from "@/components/cinema/Chapter";
import { Reveal, LineReveal } from "@/components/cinema/Reveal";

const roles = [
  { icon: Siren, title: "Emergency Technicians", desc: "First on site, trained to stabilize a loss, stop further damage and protect what matters most in the critical first hours." },
  { icon: ClipboardList, title: "Contents Specialists", desc: "Room-by-room inventory, photographic documentation and claim-ready reports that hold up to adjuster scrutiny." },
  { icon: Boxes, title: "Pack-Out Crews", desc: "Professional packing, labeling and chain-of-custody handling. Every carton tracked from the property to secure storage." },
  { icon: Truck, title: "Logistics Coordinators", desc: "Transport, secure storage and scheduling — matched to your restoration timeline so nothing slips." },
  { icon: Users, title: "Project Managers", desc: "A single point of contact from first call to final walk-through. They talk to the adjuster, the contractor and you." },
  { icon: ShieldCheck, title: "Quality & Compliance", desc: "Behind-the-scenes review of every project so paperwork, safety and workmanship stay consistent — every job, every time." },
];

const commitments = [
  { icon: Clock, title: "Answer the phone.", desc: "Our line is live 24/7. A coordinator picks up, not a voicemail, not a bot." },
  { icon: HeartHandshake, title: "Handle it like your family's.", desc: "Every box, heirloom and document is handled with the discipline of a contents specialist, not a moving truck." },
  { icon: Eye, title: "Document everything.", desc: "Photos, labels, condition notes and inventories that make claims easier and disputes rare." },
];

export default function Team() {
  return (
    <div data-testid="page-team" className="bg-[hsl(var(--gaza-teal-deep))] text-white">
      <Chapter img={IMG.iceStorm} overlay="left" testId="team-header">
        <div className="max-w-4xl">
          <Reveal y={16}><div className="eyebrow eyebrow-light mb-6">Our Team</div></Reveal>
          <LineReveal as="h1" className="display-serif text-4xl sm:text-5xl md:text-6xl leading-[0.95]" lines={["A coordinated crew,", "ready before the", "phone stops ringing."]} />
          <Reveal delay={0.5}>
            <p className="text-white/80 text-lg mt-8 leading-relaxed max-w-2xl">
              Emergency technicians, contents specialists, project managers and logistics coordinators, working as one team so recovery feels calm instead of chaotic.
            </p>
          </Reveal>
        </div>
      </Chapter>

      <Chapter img={IMG.warehouseContents} overlay="dark" testId="team-roles">
        <div className="grid md:grid-cols-12 gap-10 items-end mb-16">
          <div className="md:col-span-5">
            <Reveal y={16}><div className="eyebrow eyebrow-light mb-5">How we're organized</div></Reveal>
            <LineReveal className="display-serif text-4xl md:text-5xl leading-[0.98]" lines={["Specialists for", "every stage of", "the recovery."]} />
          </div>
          <Reveal delay={0.4} className="md:col-span-6 md:col-start-7 text-white/80 leading-relaxed text-lg">
            Every BackHome project runs through the same disciplined lanes. The right people show up at the right moment, keeping the property, the contents and the paperwork moving forward together.
          </Reveal>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {roles.map((r, i) => (
            <Reveal key={r.title} delay={0.15 + i * 0.08} className="h-full">
              <article className="glass-dark h-full p-8 md:p-10 group hover:bg-[hsl(var(--gaza-teal-deep))]/80 transition" data-testid={`role-card-${r.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div className="flex items-center gap-3 mb-6">
                  <span className="w-11 h-11 grid place-items-center bg-[hsl(var(--gaza-blue-bright))]"><r.icon className="w-5 h-5 text-white" strokeWidth={1.7} /></span>
                  <span className="text-[10px] tracking-[0.28em] uppercase font-extrabold text-white/55">0{i + 1}</span>
                </div>
                <h3 className="display-serif text-2xl leading-tight">{r.title}</h3>
                <p className="text-white/75 mt-3 leading-relaxed text-base">{r.desc}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </Chapter>

      <Chapter img={IMG.contentsPackout} overlay="dark" testId="team-commitments">
        <div className="max-w-3xl mb-14">
          <Reveal y={16}><div className="eyebrow eyebrow-light mb-5">Our commitments</div></Reveal>
          <LineReveal className="display-serif text-4xl md:text-5xl leading-[0.98]" lines={["Three things every", "member of the", "team promises."]} />
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {commitments.map((c, i) => (
            <Reveal key={c.title} delay={0.2 + i * 0.12} className="h-full">
              <div className="glass-dark h-full p-10 hover:bg-[hsl(var(--gaza-teal-deep))]/80 transition" data-testid={`commitment-${i + 1}`}>
                <c.icon className="w-8 h-8 text-[hsl(var(--gaza-blue-bright))]" strokeWidth={1.4} />
                <div className="mt-6 gold-divider"></div>
                <h3 className="display-serif text-2xl mt-5 leading-tight">{c.title}</h3>
                <p className="text-white/75 mt-3 leading-relaxed">{c.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Chapter>

      <Chapter img={IMG.stageBackHome} overlay="center" className="chapter-compact" testId="team-cta">
        <div className="text-center flex flex-col items-center">
          <LineReveal className="display-serif text-3xl md:text-5xl max-w-3xl leading-[0.98]" lines={["Work with our team."]} />
          <Reveal delay={0.3}>
            <p className="text-white/80 mt-5 max-w-xl mx-auto leading-relaxed">
              Coordinators are on the line 24/7. Reach us for emergency response, contents assessment or a project scoped in advance.
            </p>
          </Reveal>
          <Reveal delay={0.45}>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <a href={SITE.phoneHref} className="btn-gold" data-testid="team-call-btn"><Phone className="w-4 h-4" /> Call {SITE.phone}</a>
              <Link to="/contact" className="btn-outline" data-testid="team-contact-btn">Contact Us <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </Reveal>
        </div>
      </Chapter>
    </div>
  );
}
